import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ManualEntry = ({ onManualScan, isProcessing }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [isOverrideMode, setIsOverrideMode] = useState(false);

  const handleManualSubmit = (e) => {
    e?.preventDefault();
    if (!manualCode?.trim()) return;

    const mockResult = {
      id: 'MANUAL-' + Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase(),
      type: 'entry',
      isValid: true,
      timestamp: new Date(),
      isManual: true,
      overrideReason: isOverrideMode ? overrideReason : null,
      data: {
        ticketId: manualCode?.toUpperCase(),
        eventName: 'Summer Music Festival 2024',
        attendeeName: 'Manual Entry',
        seatSection: 'General Admission'
      }
    };

    onManualScan(mockResult);
    setManualCode('');
    setOverrideReason('');
    setIsOverrideMode(false);
    setIsExpanded(false);
  };

  const handleOverrideToggle = () => {
    setIsOverrideMode(!isOverrideMode);
    setOverrideReason('');
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Edit3" size={18} className="text-muted-foreground" />
          <h3 className="text-sm font-medium text-foreground">Manual Entry</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-muted-foreground hover:text-foreground"
        />
      </div>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          <div className="bg-muted rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} className="text-primary mt-0.5" />
              <div className="text-xs text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Manual Entry Guidelines:</p>
                <ul className="space-y-1">
                  <li>• Use for damaged or unreadable QR codes</li>
                  <li>• Enter ticket ID or confirmation number</li>
                  <li>• Override mode requires justification</li>
                  <li>• All manual entries are logged for audit</li>
                </ul>
              </div>
            </div>
          </div>

          <form onSubmit={handleManualSubmit} className="space-y-4">
            <Input
              label="Ticket ID or Code"
              type="text"
              placeholder="Enter ticket ID, confirmation number, or QR data"
              value={manualCode}
              onChange={(e) => setManualCode(e?.target?.value)}
              required
              className="font-mono"
            />

            {/* Override Mode Toggle */}
            <div className="flex items-center justify-between p-3 bg-warning bg-opacity-10 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-warning" />
                <span className="text-sm font-medium text-foreground">Override Mode</span>
              </div>
              <Button
                type="button"
                variant={isOverrideMode ? "warning" : "outline"}
                size="sm"
                onClick={handleOverrideToggle}
              >
                {isOverrideMode ? 'Enabled' : 'Disabled'}
              </Button>
            </div>

            {/* Override Reason */}
            {isOverrideMode && (
              <Input
                label="Override Reason"
                type="text"
                placeholder="Explain why manual override is necessary"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e?.target?.value)}
                required
                description="This reason will be logged for audit purposes"
              />
            )}

            {/* Submit Buttons */}
            <div className="flex space-x-3">
              <Button
                type="submit"
                variant="default"
                loading={isProcessing}
                disabled={!manualCode?.trim() || (isOverrideMode && !overrideReason?.trim())}
                iconName="Check"
                className="flex-1"
              >
                Process Entry
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setManualCode('');
                  setOverrideReason('');
                  setIsOverrideMode(false);
                }}
                iconName="X"
              >
                Clear
              </Button>
            </div>
          </form>

          {/* Emergency Contact */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Icon name="Phone" size={14} />
              <span>Emergency Support: (555) 123-4567</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualEntry;